import axios from "axios";
import { getUserEnv } from "../utils/getUserEnv";
import { ENV } from "../constants/env";
import { API_TIMEOUT_MS } from "../constants/constants";
import {
  getLocaleFromPath,
  getBaseLang,
  getCurrency,
  DEFAULT_LOCALE,
} from "../../config/locales";

const env = getUserEnv();
export const BASE_URL =
  env === ENV.LOCAL
    ? "http://localhost:8000/"
    : env === ENV.DEV
    ? "https://dev-api.talegacyremembered.com/"
    : "https://api.talegacyremembered.com/";

export const PAYMENT_GATEWAY_PUBLIC_KEY =
  window.location.host.startsWith("localhost") ||
  window.location.host.startsWith("dev-")
    ? process.env.REACT_APP_PAYMENT_GATEWAY_PUBLIC_KEY_DEV
    : process.env.REACT_APP_PAYMENT_GATEWAY_PUBLIC_KEY_PROD;
export const MEDIA_URL = BASE_URL.slice(0, -1);

axios.defaults.baseURL = BASE_URL + "api/";
axios.defaults.timeout = API_TIMEOUT_MS;
const _activeLocale =
  getLocaleFromPath(window.location.pathname) ?? DEFAULT_LOCALE;
axios.defaults.headers.common["lng"] = getBaseLang(_activeLocale);
axios.defaults.headers.common["currency"] = getCurrency(_activeLocale);

export function setDefaultHeader(value) {
  axios.defaults.headers.common["Authorization"] = value;
}

export function postGenerateBook(data, returnFile = false) {
  const config = {};

  if (returnFile) {
    config.responseType = "blob";
  }

  return axios.post("postGenerateBook", data, config);
}

export function postCheckAllChaptersSubmitted(data) {
  return axios.post("postCheckAllChaptersSubmitted", data);
}

// Auth
export function postRefreshToken() {
  return axios.post("refreshToken");
}
export function postRevokeToken() {
  return axios.post("revokeToken");
}
export function getUser() {
  return axios.get("getUser");
}
export function postUpSertUser(data) {
  return axios.post("postUpSertUser", data);
}
export function postLogin(data) {
  return axios.post("login", data);
}
export function postUpdateBookAttribute(data) {
  return axios.post("updateBookAttribute", {
    id: data.id,
    attribute: data.attribute,
    value: data.value,
  });
}
export function postUpdateKeyCharacters(data) {
  return axios.post("updateKeyCharacters", {
    id: data.id,
    key_characters: data.key_characters,
  });
}
export function postRegister(data) {
  return axios.post("register", data);
}
export function postVerifyRegistration(data) {
  return axios.post("verifyRegistration", data);
}
export function postVerifyRegistrationWithoutOtp(data) {
  return axios.post("verifyRegistrationWithoutOtp", data);
}
export function postForgotPassword(data) {
  return axios.post("forgotPassword", data);
}
export function postResetPassword(data) {
  return axios.post("resetPassword", data);
}

export function getAllServices() {
  return axios.get("getAllServices");
}
export function getGeneralCategoriesById(id) {
  return axios.get("getGeneralCategoriesById/" + id);
}
export function getGeneralCategoryLookupsById(id) {
  return axios.get("getGeneralCategoryLookupsById/" + id);
}

export function postGetGenerateCategoriesWithQuestions(
  data,
  isLoggedIn = false,
) {
  return axios.post(
    "getGenerateCategoriesWithQuestions" + (isLoggedIn ? "" : "AsGuest"),
    data,
  );
}

export function getTotalUsersCount() {
  return axios.get("getTotalUsersCount");
}

export function getSession(id) {
  return axios.get("getSession/" + id);
}

export function getUserSessions() {
  return axios.get("getUserSessions");
}

export function upSertSession(data) {
  return axios.post("upSertSession", data);
}

export function deleteSessionById(data) {
  return axios.post("deleteSessionById", data);
}

export function postCreatePaymentForm(data) {
  return axios.post("createPaymentForm", data);
}
export function postGetPaymentForm(data) {
  return axios.post("getPaymentForm", data);
}
export function postCreateGiftCardPaymentForm(data) {
  return axios.post("createGiftCardPaymentForm", data);
}
export function getOnPaymentCallback(service_session_id) {
  return axios.get("onPaymentCallback/" + service_session_id);
}
export function getOnGiftCardPaymentCallback(plan_id, order_id, email) {
  return axios.get(`onGiftCardPaymentCallback/${plan_id}/${order_id}/${email}`);
}

export function getUserSubscription() {
  return axios.get("userSubscription");
}

export function getSubscriptionDetails() {
  return axios.get("subscriptionDetails");
}

export function postCancelSubscription(data) {
  return axios.post("cancelSubscription", data);
}

export function postCreateSubscriptionPaymentForm(data) {
  return axios.post("createSubscriptionPaymentForm", data);
}

export function postCheckSubscriptionCoupon(data) {
  return axios.post("checkSubscriptionCoupon", data);
}

export function getSubscriptionPaymentStatusCallback(order_id) {
  return axios.get("subscriptionPaymentStatusCallback/" + order_id);
}

export function postUploadDocx(data) {
  return axios.post("uploadDocx", data);
}

export function postUploadDocxForProofreading(data) {
  return axios.post("uploadDocxForProofreading", data);
}

export function postSubmitDocxForPrinting(data) {
  return axios.post("submitDocxForPrinting", data);
}

export function postSendPrintingOrderEmail(printingOrderId) {
  return axios.post("sendPrintingOrderEmail/" + printingOrderId);
}

export function postCreatePrintingPaymentForm(data) {
  return axios.post("createPrintingPaymentForm", data);
}

// ===== Card management =====

export function getPaymentMethod() {
  return axios.get("paymentMethod");
}

export function postCreateUpdatePaymentMethodForm() {
  return axios.post("updatePaymentMethod");
}

export function getUpdatePaymentMethodCallback(order_id) {
  return axios.get("updatePaymentMethodCallback/" + order_id);
}

export function getPrintingPaymentStatusCallback(order_id) {
  return axios.get("printingPaymentStatusCallback/" + order_id);
}

export function postCreateAudiobookPaymentForm(data) {
  return axios.post("create-audiobook-payment-form", data);
}

export function postUploadAndAnalyzeDocx(data) {
  return axios.post("uploadAndAnalyzeDocx", data);
}

export function getAudiobookPaymentStatusCallback(order_id) {
  return axios.get("audiobook-payment-status-callback/" + order_id);
}

export function postGenerateAICover(serviceSessionId) {
  return axios.post("generateAICoverImage", {
    id: serviceSessionId,
  });
}

export function postGenerateAIChapterImage(serviceSessionId, chapterId) {
  return axios.post("generateAIChapterImage", {
    id: serviceSessionId,
    chapter_id: chapterId,
  });
}

export function getTools() {
  return axios.get("/tools");
}

// Plans
export function getPlansForGift() {
  return axios.get("getPlansForGift");
}

// Coupon
export function postCheckCouponForPayment(data) {
  return axios.post("checkCouponForPayment", data);
}

export function postCheckAudiobookCoupon(data) {
  return axios.post("checkAudiobookCoupon", data);
}

// ElevenLabs
export function postGenerateAudioBook(payload) {
  return axios.post("generateAudiobook", payload);
}

export function postGeneratePreviewAudiobook(payload) {
  return axios.post("generatePreviewAudiobook", payload);
}

export function postCheckAudiobookQuota(data) {
  return axios.post("checkAudiobookQuota", data);
}

// Chat-GPT
export function postGetStoryAnswer(data) {
  return axios.post("getStoryAnswer", data);
}

export function postGetOptimizedTranscript(data) {
  return axios.post("getOptimizedTranscript", data);
}

export function postProcessSelectedText(data) {
  return axios.post("processSelectedText", data);
}

// Google Cloud Speech To Text
export async function postGetGoogleSpeechToText(data) {
  return axios.post("getGoogleSpeechToText", data);
}

// Feedback
export function postFeedback(data) {
  return axios.post("feedback", data);
}
